import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const PIN_LENGTH = 4;

export default function AdminPinPage() {
  const [pin, setPin] = useState(Array(PIN_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);
  const { verifyAdminPin, resendOtp, currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  const isAdmin = userProfile?.role === 'superAdmin' || userProfile?.role === 'admin';

  useEffect(() => {
    if (!currentUser) navigate('/login', { replace: true });
    inputRefs.current[0]?.focus();
  }, [currentUser, navigate]);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    if (value && index < PIN_LENGTH - 1) inputRefs.current[index + 1]?.focus();
    if (newPin.every((d) => d !== '') && value) handleVerify(newPin.join(''));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handleVerify = async (code) => {
    setLoading(true);
    try {
      const verified = await verifyAdminPin(code);
      if (verified) {
        if (isAdmin) {
          try {
            await resendOtp();
            toast.success('PIN verified. Enter SMS code.');
            navigate('/verify-otp', { state: { adminOtp: true } });
          } catch (otpErr) {
            console.error('Failed to send admin OTP:', otpErr);
            toast.success('Welcome, Admin.');
            navigate('/admin');
          }
        } else {
          toast.success('Welcome back!');
          navigate('/');
        }
      } else {
        toast.error('Invalid PIN.');
        setPin(Array(PIN_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      toast.error('Verification failed.');
      setPin(Array(PIN_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Theme classes based on isAdmin
  const bgClass = isAdmin ? 'bg-brand-dark' : 'bg-brand-cream';
  const titleClass = isAdmin ? 'text-white' : 'text-brand-dark';
  const subtitleClass = isAdmin ? 'text-white/50' : 'text-body';

  return (
    <div className={`min-h-screen flex items-center justify-center px-5 py-8 md:px-6 md:py-12 ${bgClass}`}>
      <div className="w-full max-w-md text-center">
        <div className="text-5xl mb-6">🔐</div>
        <h1 className={`font-serif text-3xl md:text-4xl mb-3 tracking-tight ${titleClass}`}>
          Security PIN
        </h1>
        <p className={`text-base leading-relaxed mb-10 ${subtitleClass}`}>
          Enter your 4-digit security PIN to continue.
        </p>

        <div className="flex gap-3 sm:gap-4 justify-center mb-8">
          {pin.map((digit, i) => {
            // PIN input theme classes
            const inputBorder = digit ? 'border-brand-primary' : 'border-slate-200';
            const inputText = isAdmin ? 'text-white' : 'text-brand-dark';
            const inputBg = isAdmin
              ? digit ? 'bg-brand-primary/10' : 'bg-white/[0.06]'
              : digit ? 'bg-brand-primary/[0.04]' : 'bg-white';
            return (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                disabled={loading}
                className={`w-12 h-14 sm:w-14 sm:h-16 rounded-2xl border-2 text-2xl sm:text-3xl font-bold text-center outline-none transition-colors ${inputBorder} ${inputText} ${inputBg}`}
              />
            );
          })}
        </div>

        {loading && (
          <div className="flex justify-center">
            <span className="spinner" />
          </div>
        )}
      </div>
    </div>
  );
}